import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import FileNameList from './FileNameList';
import FilePickerButton from './FilePickerButton';
import UploadCard from './UploadCard';

interface DetailedUploadCardProps {
  category: string;
  categoryOptions: { value: string; label: string }[];
  selectedFiles: File[];
  uploading: boolean;
  onCategoryChange: (value: string) => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onStartUpload: () => void;
}

const CATEGORY_ID = 'test-upload-category';

const megabytes = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(2)} MB`;

const DetailedUploadCard: React.FC<DetailedUploadCardProps> = ({
  category,
  categoryOptions,
  selectedFiles,
  uploading,
  onCategoryChange,
  onFileSelect,
  onStartUpload,
}) => {
  const { t } = useTranslation();

  return (
    <UploadCard title={t('testUpload.detailedUpload')}>
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <Label
            htmlFor={CATEGORY_ID}
            className="revy-cjk text-xs leading-[1.5] text-muted-foreground"
          >
            {t('testUpload.category')}
          </Label>
          <Select value={category} onValueChange={onCategoryChange} disabled={uploading}>
            <SelectTrigger id={CATEGORY_ID} className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="revy-ui">
              {categoryOptions.map((option) => (
                <SelectItem key={option.value} value={option.value} className="revy-cjk">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <FilePickerButton
          label={t('testUpload.selectFiles')}
          accept=".pdf,.docx,.xlsx"
          disabled={uploading}
          onSelect={onFileSelect}
        />
      </div>

      {selectedFiles.length > 0 && (
        <div className="mb-4">
          <h3 className="revy-cjk mb-2 text-xs font-semibold leading-[1.5] text-muted-foreground">
            {t('testUpload.selectedFiles', { count: selectedFiles.length })}
          </h3>
          <FileNameList
            items={selectedFiles.map((file, index) => ({
              id: `${index}-${file.name}`,
              primary: file.name,
              secondary: megabytes(file.size),
              icon: <FileText className="size-4 text-muted-foreground" strokeWidth={1.9} />,
            }))}
          />
        </div>
      )}

      <Button onClick={onStartUpload} disabled={uploading || selectedFiles.length === 0}>
        <Play strokeWidth={1.9} />
        <span className="revy-cjk">{t('testUpload.startUpload')}</span>
      </Button>
    </UploadCard>
  );
};

export default DetailedUploadCard;
